import type { ComponentProps } from 'react'
import React, { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import ContactContext from 'contexts/contactContext'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'

interface Props {
  minified: boolean
}

export default function InfantSeatNoticeMessage(props: Props) {
  const { minified } = props

  const intl = useIntl()
  const contact = useContext(ContactContext)
  const showModal = useContext(ModalContext)

  const onRequestCallback = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS" />)
  }, [showModal])

  const bannerProps: Partial<ComponentProps<typeof MessageBanner>> = useMemo(() => {
    if (minified) return {}

    return {
      heading: intl.formatMessage({
        id: 'cJp0a2y3wt',
        defaultMessage: 'Travel with infant?',
        description: 'Heading for infant seating notice banner',
      }),
      primaryActionButtonProps: {
        children: intl.formatMessage({
          id: '+Azc/KZ4JN',
          defaultMessage: 'Request a callback',
          description: 'Button to request a callback for infant seating assistance',
        }),
        size: 'small' as const,
        onClick: onRequestCallback,
      },
      secondaryActionButtonProps: {
        children: contact.local.humanReadable,
        href: `tel:${contact.local.number}`,
        size: 'small' as const,
      },
    }
  }, [minified, intl, onRequestCallback, contact.local.humanReadable, contact.local.number])

  const contactLinkTelHref = `tel:${contact.local.number}`
  const contactLink = useCallback(
    (chunks: React.ReactNode) => minified
      ? <TextLink href={contactLinkTelHref}>{chunks}</TextLink>
      : <>{chunks}</>,
    [minified, contactLinkTelHref],
  )

  return (
    <MessageBanner {...bannerProps} kind="warning">
      <FormattedMessage
        id="1kgPoChWWv"
        defaultMessage="1 adult can travel with 1 infant on their lap. To book a seat for an infant, <contactLink>contact us 24/7</contactLink>."
        description="Warning about infant seating policy with contact information"
        values={{ contactLink }}
      />
    </MessageBanner>
  )
}
